import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import DocumentPreviewModal from "../../components/DocumentPreviewModal";
import EmployeeSearch from "../../components/EmployeeSearch";
import RemoveDocumentButton from "../../components/RemoveDocumentButton";
import EmailDocumentButton from "../../components/EmailDocumentButton";
import SignatureModal from "../../components/SignatureModal";
import SignatureDetailsModal from "../../components/SignatureDetailsModal";
import useFilterParams from "../../hooks/useFilterParams";
import { useTabParam } from "../../hooks/useTabParam";
import { PREVIEWABLE_TYPES, openDocument } from "../../utils/documentFiles";

interface Doc { id: number; title: string; employee_id: number | null; category_id: number; file_name: string; file_type: string; file_size_bytes: number; version: number; expires_at: string | null; requires_signature: boolean; requires_initials: boolean; signed_at: string | null; signature_type: string | null; signature_mode: string | null; signature_reference: string | null; acknowledged_at: string | null; is_active: boolean; created_at: string; description: string | null; can_remove?: boolean; last_emailed_at?: string | null; }
interface Category { id: number; name: string; requires_acknowledgement: boolean; }

const DOC_TABS = ["all", "expiring"] as const;
const DOC_FILTERS = { cat: "" };

export default function AdminDocumentsPage() {
  const [docs, setDocs] = useState<Doc[]>([]);
  const [expiring, setExpiring] = useState<Doc[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tab, setTab] = useTabParam(DOC_TABS, "all");
  const [showUpload, setShowUpload] = useState(false);
  const [filters, setFilter] = useFilterParams(DOC_FILTERS);
  const [signingDoc, setSigningDoc] = useState<Doc | null>(null);
  const [verifyDocId, setVerifyDocId] = useState<number | null>(null);
  const [previewDoc, setPreviewDoc] = useState<Doc | null>(null);

  const load = useCallback(async () => {
    try {
      const [d, e, c] = await Promise.all([
        api.get<Doc[]>("/api/documents"),
        api.get<Doc[]>("/api/documents/expiring"),
        api.get<Category[]>("/api/documents/categories"),
      ]);
      setDocs(d); setExpiring(e); setCategories(c);
    } catch {}
  }, []);
  useEffect(() => { void load(); }, [load]);

  const filtered = filters.cat ? docs.filter((d) => String(d.category_id) === filters.cat) : docs;
  const catName = (id: number) => categories.find((c) => c.id === id)?.name ?? "";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Documents</h1>
        <button type="button" onClick={() => setShowUpload(true)} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ Upload Document</button>
      </div>

      <nav className="flex gap-1 border-b border-gray-200">
        <TabBtn active={tab === "all"} onClick={() => setTab("all")}>All ({docs.length})</TabBtn>
        <TabBtn active={tab === "expiring"} onClick={() => setTab("expiring")}>Expiring ({expiring.length})</TabBtn>
      </nav>

      {tab === "all" && (
        <div className="flex gap-2">
          <select value={filters.cat} onChange={(e) => setFilter("cat", e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {showUpload && <UploadForm categories={categories} onDone={() => { setShowUpload(false); void load(); }} onCancel={() => setShowUpload(false)} />}

      <div className="space-y-2">
        {(tab === "all" ? filtered : expiring).map((d) => (
          <div key={d.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">{d.title}</p>
                <p className="text-xs text-gray-500">{catName(d.category_id)} · {d.file_name} · {d.file_type.toUpperCase()} · v{d.version}</p>
                <p className="text-xs text-gray-400">{d.employee_id ? `Employee #${d.employee_id}` : "Company-wide"} · {new Date(d.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                {d.expires_at && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${new Date(d.expires_at) < new Date() ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>Expires: {d.expires_at}</span>}
                {d.requires_signature && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${d.signed_at ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-800"}`}>{d.signed_at ? "Signed" : "Signature Required"}</span>}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {PREVIEWABLE_TYPES.has(d.file_type.toLowerCase()) && (
                <button type="button" onClick={() => setPreviewDoc(d)} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">View</button>
              )}
              <button type="button" onClick={() => void openDocument(d.id, "attachment")} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">Download</button>
              {d.requires_signature && !d.signed_at && (
                <button type="button" onClick={() => setSigningDoc(d)} className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600">Sign</button>
              )}
              {d.signed_at && (
                <button type="button" onClick={() => setVerifyDocId(d.id)} className="rounded-lg border border-green-200 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50">Verify Signature</button>
              )}
              {d.employee_id !== null && (
                <EmailDocumentButton
                  documentId={d.id}
                  needsSignature={d.requires_signature && !d.signed_at}
                  lastEmailedAt={d.last_emailed_at}
                />
              )}
              {d.can_remove && (
                <RemoveDocumentButton documentId={d.id} title={d.title} onRemoved={() => void load()} />
              )}
            </div>
          </div>
        ))}
        {(tab === "all" ? filtered : expiring).length === 0 && <p className="text-sm text-gray-400">No documents.</p>}
      </div>

      {signingDoc && (
        <SignatureModal
          doc={signingDoc}
          onClose={(signed) => {
            setSigningDoc(null);
            if (signed) void load();
          }}
        />
      )}

      {verifyDocId !== null && (
        <SignatureDetailsModal
          documentId={verifyDocId}
          onClose={() => setVerifyDocId(null)}
        />
      )}

      {previewDoc && (
        <DocumentPreviewModal
          documentId={previewDoc.id}
          title={previewDoc.title}
          fileType={previewDoc.file_type}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}

function UploadForm({ categories, onDone, onCancel }: { categories: { id: number; name: string }[]; onDone: () => void; onCancel: () => void }) {
  const [scope, setScope] = useState<"employee" | "company">("employee");
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [visibility, setVisibility] = useState<string>("global");
  const [categoryId, setCategoryId] = useState<string>(categories[0]?.id?.toString() ?? "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [requiresSig, setRequiresSig] = useState(false);
  const [signatureMode, setSignatureMode] = useState("confirm_receipt");
  const [requiresInitials, setRequiresInitials] = useState(false);
  const [notify, setNotify] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError("Select a file"); return; }
    if (scope === "employee" && !employeeId) { setError("Select an employee"); return; }
    setSubmitting(true); setError(null);
    try {
      const params = new URLSearchParams({
        title, category_id: categoryId, requires_signature: String(requiresSig),
      });
      if (requiresSig) {
        params.set("signature_mode", signatureMode);
        if (signatureMode === "on_document" && requiresInitials) {
          params.set("requires_initials", "true");
        }
      }
      if (scope === "employee" && employeeId) {
        params.set("employee_id", String(employeeId));
        params.set("notify", String(notify));
      } else if (scope === "company") {
        params.set("visibility", visibility);
      }
      if (description) params.set("description", description);
      if (expiresAt) params.set("expires_at", expiresAt);

      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`/api/documents?${params.toString()}`, {
        method: "POST", credentials: "include", body: form,
      });
      if (!res.ok) { const b = await res.json().catch(() => null); throw new Error(b?.detail ?? "Upload failed"); }
      onDone();
    } catch (err) { setError((err as Error).message); } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">Upload Document</h3>
      <div className="flex gap-3 text-sm">
        <label className="flex items-center gap-1"><input type="radio" checked={scope === "employee"} onChange={() => setScope("employee")} /> Employee-specific</label>
        <label className="flex items-center gap-1"><input type="radio" checked={scope === "company"} onChange={() => setScope("company")} /> Company-wide</label>
      </div>
      {scope === "employee" && (
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Employee</span>
          <EmployeeSearch value={employeeId} onChange={setEmployeeId} placeholder="Search by name..." className="input mt-1" />
        </label>
      )}
      {scope === "company" && (
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Visibility</span>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="input mt-1">
            <option value="global">Global (all employees)</option>
            <option value="site">Site-scoped</option>
            <option value="region">Region-scoped</option>
          </select>
        </label>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Category</span>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="input mt-1">{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
        </label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Employment Contract" className="input mt-1" />
        </label>
      </div>
      <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Description</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="input mt-1" />
      </label>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Expiry Date (optional)</span>
          <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="input mt-1" />
        </label>
        <label className="flex items-center gap-2 text-sm pt-5"><input type="checkbox" checked={requiresSig} onChange={(e) => setRequiresSig(e.target.checked)} /> Requires e-signature</label>
      </div>
      {requiresSig && (
        <>
          <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Signature Mode</span>
            <select value={signatureMode} onChange={(e) => setSignatureMode(e.target.value)} className="input mt-1">
              <option value="confirm_receipt">Confirm Receipt</option>
              <option value="on_document">On Document</option>
            </select>
          </label>
          {signatureMode === "on_document" && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={requiresInitials} onChange={(e) => setRequiresInitials(e.target.checked)} />
              Requires initials
            </label>
          )}
        </>
      )}
      <label className="block text-sm"><span className="text-xs font-medium text-gray-700">File</span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={(e) => setFile(e.target.files?.[0] ?? null)} required className="mt-1 block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-pme-blue file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white" />
      </label>
      {scope === "employee" && (
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="mt-0.5" />
          <span>
            Email the employee now
            <span className="block text-xs text-gray-500">Untick to upload quietly and send later with "Email for signature" on the document.</span>
          </span>
        </label>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{submitting ? "Uploading..." : "Upload"}</button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} className={["px-4 py-2 text-sm font-semibold", active ? "border-b-2 border-pme-blue text-pme-blue" : "text-gray-500"].join(" ")}>{children}</button>;
}
